import CIcon from '@coreui/icons-react';
import { CBadge, CButton } from '@coreui/react';
import DataTable from 'components/common/DataTable';
import Pagination from 'components/common/Pagination';
import { get, inRange } from 'lodash';
import { useMemo } from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import { formatDate } from 'utils/helpers';
import { cilReload, cilInfo } from '@coreui/icons';
import useRetryApiRequest from 'hooks/hypoNoeApiRequest/userRetryHypoNoeApiRequest';
import type {
  THypoNoeApiRequest,
  THypoNoeApiRequests,
} from 'hooks/hypoNoeApiRequest/useGetHypoNoeApiRequestsReport';
import requireValue from 'utils/requireValue';
import s from './HypoNoeApiRequestsTable.module.scss';

/** A row of the HYPO NOE API requests table. */
interface IApiRequestRow {
  email: string;
  error: string | null;
  status: number;
  user_info: string;
  created_at: string;
  requestId: string;
  project_name: unknown;
  project_type: unknown;
}

interface IAPIRequestsTableProps {
  requests: THypoNoeApiRequests;
  showModal: (modalProps: { requestId: string }) => void;
  changePage: (page: number) => void;
  currentPage: number;
  total: number | null;
}

const userInfoOf = (
  user: THypoNoeApiRequest['user'],
  email: string | null,
  t: IntlShape['formatMessage'],
): string => {
  if (!user) return t({ id: 'user.user_not_found' });
  const { firstName, lastName } = requireValue(user.profile, 'firstName');
  return `${firstName} ${lastName} \n ${email}`;
};

const APIRequestsTable = ({
  requests,
  showModal,
  changePage,
  currentPage,
  total,
}: IAPIRequestsTableProps) => {
  const { formatMessage: t } = useIntl();
  const onRetryRequest = useRetryApiRequest();

  const fields = useMemo(
    () => [
      { key: 'user_info', label: t({ id: 'hypo_noe_api_request.user_info' }) },
      { key: 'project_name', label: t({ id: 'hypo_noe_api_request.project_name' }) },
      { key: 'project_type', label: t({ id: 'hypo_noe_api_request.project_type' }) },
      { key: 'request_details', label: t({ id: 'hypo_noe_api_request.details' }) },
      { key: 'created_at', label: t({ id: 'hypo_noe_api_request.created_at' }) },
      { key: 'status', label: t({ id: 'hypo_noe_api_request.status' }) },
      { key: 'error', label: t({ id: 'hypo_noe_api_request.error' }) },
      { key: 'retry_request', label: t({ id: 'hypo_noe_api_request.retry_request' }) },
    ],
    [t],
  );

  const items = useMemo(
    () =>
      // A missing list or creator profile throws, as it did.
      requireValue(requests, 'map').map(({ _id, request_params, response, user, created_at }) => {
        const { project_type, project_name } = request_params;
        const { status, error } = response;
        const email = get(user, 'emails[0].address', '');
        const userInfo = userInfoOf(user, email, t);

        const row: IApiRequestRow = {
          email,
          error,
          status,
          user_info: userInfo,
          created_at: formatDate(created_at),
          requestId: _id,
          project_name: project_name.value,
          project_type: project_type.value,
        };
        return row;
      }),
    [requests, t],
  );

  return (
    <>
      <DataTable
        items={items}
        fields={fields}
        size="sm"
        addTableClasses={s.table}
        scopedSlots={{
          retry_request: ({ status, requestId }: IApiRequestRow) => {
            if (status === 200 || status === 202) return <td />;
            return (
              <td className="text-center">
                <CButton className={s.iconButton} onClick={() => onRetryRequest(requestId)}>
                  <CIcon icon={cilReload} className="mt-0" />
                </CButton>
              </td>
            );
          },
          status: ({ status }: IApiRequestRow) => (
            <td>
              <CBadge
                shape="rounded-pill"
                color={`${inRange(status, 200, 300) ? 'success' : 'danger'}`}
                style={{ fontSize: '14px' }}
              >
                {`${status}`}
              </CBadge>
            </td>
          ),
          request_details: ({ requestId }: IApiRequestRow) => (
            <td>
              <CButton onClick={() => showModal({ requestId })} className={s.iconButton}>
                <CIcon icon={cilInfo} />
              </CButton>
            </td>
          ),
        }}
      />
      <Pagination {...{ changePage, currentPage, total }} />
    </>
  );
};

export default APIRequestsTable;
